import React from 'react';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { WidgetContainer } from '@frontend/app/refresh-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@frontend/shadcn/lib/utils';
import { useAnalyticsParams, usePostsData } from '@frontend/applications/Social/Analytics/hooks';
import { useAuth } from '@frontend/context/authContext';
import { useGetCurrentClient } from '@frontend/app/hooks';
import { useFilterFormData } from '@frontend/applications/Social/Analytics/components/filters-form/hooks/useFilterFormData';
import { ImpressionsCard } from './ImpressionsCard';
import { EngagementCard } from './EngagementCard';
import { ReachCard } from './ReachCard';
import { TMVCard } from './TMVCard';
import { PostsCreatedOvertime } from './PostsCreatedOvertime';
import { ImpressionsOvertime } from './ImpressionsOvertime';

const DEFAULT_VARIABLES = {
  dateType: 'last6Months' as const,
  postTypes: [],
  includeEstimates: true,
  networks: [],
  projectIds: [],
  groupIds: [],
  memberIds: [],
  segments: [],
  mentionSources: [],
  viewMode: 'cards' as const,
  postDateIntervalEnabled: false,
  lifetimeInsightsEnabled: false,
};

export const SocialMetrics = ({ projectId, className }: { projectId?: number; className?: string }) => {
  const { clientInfo } = useAuth();

  const { client } = useGetCurrentClient();

  const { queryVariables, initialized } =
    useAnalyticsParams({
      clientId: clientInfo.id,
      defaultVariables: DEFAULT_VARIABLES,
      clientCreatedDate: client?.createdDate,
    });

  const { projects, groups } = useFilterFormData();

  const { data, fetchingInitialPosts } = usePostsData({
    clientId: clientInfo.id,
    tab: 'posts',
    projects,
    groups,
    queryVariables: {
      ...queryVariables,
      projectIds: [projectId],
    },
    initialized,
  });

  const totalInsights = data?.client?.socialAnalytics?.totalInsights;

  const postsBreakdownData = data?.client?.socialAnalytics?.postsBreakdown;

  return (
    <WidgetContainer
      widgetTitle="Social Metrics"
      textColor="text-grey-6 text-sm"
      bgColor="bg-white"
      widgetIcon={<FontAwesomeIcon icon={faChartLine} className="text-grey-4" />}
      className={cn('h-min', className)}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4">
        <ImpressionsCard
          data={totalInsights}
          isLoading={fetchingInitialPosts}
        />
        <EngagementCard
          data={totalInsights}
          isLoading={fetchingInitialPosts}
        />
        <ReachCard
          data={totalInsights}
          isLoading={fetchingInitialPosts}
        />
        <TMVCard
          data={totalInsights}
          isLoading={fetchingInitialPosts}
        />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2">
        <PostsCreatedOvertime
          data={postsBreakdownData}
          fetching={fetchingInitialPosts}
        />
        <ImpressionsOvertime
          data={postsBreakdownData}
          fetching={fetchingInitialPosts}
        />
      </div>
    </WidgetContainer>
  );
};
